"use client";

import React from "react";
import { Shield, Lock, FileText, Eye, UserCheck, Mail } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PrivacyPolicy() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const lastUpdated = "October 24, 2023";
  const policySections = [{
    id: "item-1",
    title: "Information We Collect",
    icon: <FileText className="w-5 h-5 text-secondary" />,
    content: <div className="space-y-4 text-muted-foreground">
          <p>
            At Education Family Tutors, we collect information to provide high-quality tutoring and coaching services tailored to each student&apos;s needs. We are committed to protecting your privacy and ensuring your data is handled securely.
          </p>
          <h4 className="font-semibold text-foreground text-lg pt-2">Categories of Data We Collect</h4>
          <p>
            The types of personal information we may collect and process include:
          </p>
          <ol className="list-decimal pl-5 space-y-4">
            <li>
              <span className="font-semibold text-foreground">Personal Identification:</span>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Full names of parents and students</li>
                <li>Contact details including email addresses and phone numbers</li>
                <li>Postal addresses for billing and service coordination</li>
              </ul>
            </li>
            <li>
              <span className="font-semibold text-foreground">Academic & Coaching Information:</span>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Current school year, subjects of interest, and exam boards</li>
                <li>Current and predicted grades to tailor our teaching approach</li>
                <li>Mindset and motivation data to inform our coaching strategies</li>
              </ul>
            </li>
            <li>
              <span className="font-semibold text-foreground">Payment & Technical Data:</span>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Billing details for secure payment processing via Systeme.io</li>
                <li>Technical usage data, including IP addresses and browser types</li>
              </ul>
            </li>
          </ol>
          <h4 className="font-semibold text-foreground text-lg pt-2">How We Collect Your Information</h4>
          <p>
            Most of the personal information we process is provided to us directly by you for one of the following reasons:
          </p>
          <ol className="list-decimal pl-5 space-y-2">
            <li>You have made an enquiry or booked a consultation with us.</li>
            <li>You have enrolled in one of our tutoring or coaching programmes.</li>
            <li>You have signed up for our newsletter or educational resources.</li>
          </ol>
        </div>
  }, {
    id: "item-2",
    title: "How We Use Your Data",
    icon: <Eye className="w-5 h-5 text-secondary" />,
    content: <div className="space-y-4 text-muted-foreground">
          <p>
            We use the information we collect for specific, legitimate business
            purposes to provide the best possible support for our students.
          </p>
          <ol className="list-decimal pl-5 space-y-4">
            <li>
              <span className="font-semibold text-foreground">Service Delivery & Personalisation:</span>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>To deliver our 30-Day, 12-Week, and Full Mastery tutoring and coaching programmes effectively</li>
                <li>To create personalised study plans and coaching strategies that match your child&apos;s specific needs</li>
              </ul>
            </li>
            <li>
              <span className="font-semibold text-foreground">Communication & Support:</span>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>To send progress updates and schedule consultations</li>
                <li>To respond to your inquiries via email or WhatsApp</li>
              </ul>
            </li>
            <li>
              <span className="font-semibold text-foreground">Operational Improvement:</span>
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>To analyse website usage and feedback to enhance our educational resources</li>
                <li>To maintain and improve website functionality and user experience</li>
              </ul>
            </li>
          </ol>
        </div>
  }, {
    id: "item-3",
    title: "Data Security",
    icon: <Lock className="w-5 h-5 text-secondary" />,
    content: <div className="space-y-4 text-muted-foreground">
          <p>
            We take the security of your data seriously. Education Family Tutors
            implements appropriate technical and organisational measures to
            protect your personal data against unauthorised access, alteration,
            disclosure, or destruction.
          </p>
          <p>
            We use secure, encrypted connections (SSL) for our website and rely
            on trusted third-party payment processors who adhere to strict
            security standards (PCI-DSS compliance). We do not store your full
            credit card details on our own servers.
          </p>
        </div>
  }, {
    id: "item-4",
    title: "Sharing Your Information",
    icon: <UserCheck className="w-5 h-5 text-secondary" />,
    content: <div className="space-y-4 text-muted-foreground">
          <p>
            We respect your privacy and do not sell your personal information to
            third parties. We may share your data only in the following
            circumstances:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Service Providers:</strong> With trusted third-party
              vendors who assist us in operating our website, conducting our
              business, or servicing you (e.g., payment processors, email
              platforms), so long as those parties agree to keep this
              information confidential.
            </li>
            <li>
              <strong>Legal Requirements:</strong> When required by law or to
              protect our rights, property, or safety, or that of others.
            </li>
          </ul>
        </div>
  }, {
    id: "item-5",
    title: "Your Rights",
    icon: <Shield className="w-5 h-5 text-secondary" />,
    content: <div className="space-y-4 text-muted-foreground">
          <p>
            Under UK data protection laws (GDPR), you have specific rights
            regarding your personal data:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Right to Access:</strong> You can request a copy of the
              personal data we hold about you.
            </li>
            <li>
              <strong>Right to Rectification:</strong> You can ask us to correct
              any inaccurate or incomplete data.
            </li>
            <li>
              <strong>Right to Erasure:</strong> You can request that we delete
              your personal data ("right to be forgotten") under certain
              conditions.
            </li>
            <li>
              <strong>Right to Withdraw Consent:</strong> If we process data
              based on your consent, you may withdraw that consent at any time.
            </li>
          </ul>
          <p>
            To exercise any of these rights, please contact us using the details
            provided below.
          </p>
        </div>
  }, {
    id: "item-6",
    title: "Contact Us",
    icon: <Mail className="w-5 h-5 text-secondary" />,
    content: <div className="space-y-4 text-muted-foreground">
          <p>
            If you have any questions about this Privacy Policy or how we handle
            your data, please do not hesitate to contact us.
          </p>
          <div className="mt-4 p-4 bg-muted/30 rounded-lg border border-border">
            <p className="font-semibold text-foreground">
              Education Family Tutors
            </p>
            <p>Email: support@educationfamilytutors.com</p>
            <p>
              Visit our{" "}
              <Link to="/ContactPage" className="text-primary hover:underline font-medium">
                Contact Page
              </Link>{" "}
              to book a call.
            </p>
          </div>
        </div>
  }];
  return <section id="privacypolicy" className="bg-background py-16 md:py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-30">
        <div className="absolute -top-[10%] -left-[10%] w-[40%] h-[40%] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute top-[20%] right-[5%] w-[30%] h-[30%] bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div ref={ref} className={`container mx-auto px-4 max-w-[76rem] relative z-10 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        {}
        <div className="mb-8 md:mb-12 flex justify-center md:justify-start">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/HomePage">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Privacy Policy</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-8 lg:col-start-3">
            {}
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-primary">
                Privacy Policy
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                Your privacy is important to us. This policy outlines how
                Education Family Tutors collects, uses, and protects your
                personal information when you use our website and services.
              </p>
              <p className="text-sm text-muted-foreground mt-4">
                Last Updated: {lastUpdated}
              </p>
            </div>

            {}
            <div className="bg-white/50 backdrop-blur-sm rounded-2xl shadow-soft border border-border p-6 md:p-8">
              <Accordion type="single" collapsible className="w-full">
                {policySections.map((section, index) => <AccordionItem key={section.id} value={section.id} className="border-b border-border last:border-0" data-index={index}>
                    <AccordionTrigger className="hover:no-underline hover:bg-muted/20 px-4 rounded-lg transition-colors py-5">
                      <div className="flex items-center gap-4 text-left">
                        <div className="p-2 bg-primary/5 rounded-md shrink-0">
                          {section.icon}
                        </div>
                        <span className="text-lg font-semibold text-foreground">
                          {section.title}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-6 pt-2 text-base leading-relaxed">
                      {section.content}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>

            {}
            <div className="mt-12 text-center">
              <p className="text-muted-foreground text-sm">
                By using our website and services, you consent to the terms of
                this Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}